<template>
  <div class="search-bar-wrapper position-relative" :class="wrapperClass">
    <b-input-group
      class="search-bar border"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="enterSelected"
    >
      <b-form-input
        v-model="query"
        :placeholder="placeholder"
        class="border-0 search-input shadow-none"
        @focus="handleSearchFocus"
        @blur="closeLater"
        @input="handleSearchInput"
      />
      <b-button
        type="button"
        class="search-button border-0"
        :pressed="false"
        @mousedown.prevent
        @click="enterSelected"
        aria-label="Search"
      >
        <b-icon-search></b-icon-search>
      </b-button>
    </b-input-group>

    <!-- Suggestions dropdown -->
    <div
      v-show="open && filtered.length"
      class="typeahead shadow"
      @mousedown.prevent
    >
      <button
        v-for="(item, i) in filtered"
        :key="item.id + '-' + i"
        type="button"
        class="typeahead-item"
        :class="{ active: i === highlighted }"
        @click="select(item)"
      >
        <span
          class="badge me-2"
          :class="badgeClass(item.collection)"
        >{{ prettyType(item.collection) }}</span>
        <span class="truncate">{{ displayTitle(item) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { onMounted } from "vue";
import {
  goToPeriod,
  goToTopic,
  goToEvent,
  goToPerson,
  pushRoute
} from "@/router/navigation";
import router from "@/router/index";
import store from "@/store";
import storeTopic from "@/store/topic.js";
import { db } from "@/main";
import { useSearchBar } from "@/composables/useSearchBar";

export default {
  name: "SearchBar",
  props: {
    placeholder: {
      type: String,
      default: "What do you need to study?"
    },
    wrapperClass: {
      type: String,
      default: ""
    }
  },
  setup() {
    const {
      query,
      open,
      highlighted,
      grabbingSearch,
      filtered,
      displayTitle
    } = useSearchBar();

    const prettyType = c =>
      c === "topics"
        ? "Topic"
        : c === "events"
        ? "Event"
        : c === "people"
        ? "Person"
        : c === "works"
        ? "Primary"
        : c === "terminology"
        ? "Term"
        : c;

    const badgeClass = c =>
      c === "topics"
        ? "bg-secondary"
        : c === "events"
        ? "bg-info"
        : c === "people"
        ? "bg-dark"
        : c === "works"
        ? "bg-primary"
        : c === "terminology"
        ? "bg-success"
        : "bg-light";

    const handleSearchFocus = () => {
      open.value = true;
    };

    const handleSearchInput = () => {
      open.value = true;
    };

    const closeLater = () => {
      setTimeout(() => (open.value = false), 150);
    };

    const move = dir => {
      if (!filtered.value.length) return;
      const n = filtered.value.length;
      highlighted.value = (highlighted.value + dir + n) % n;
    };

    const enterSelected = () => {
      if (filtered.value.length === 0) return;
      const item = filtered.value[highlighted.value] || filtered.value[0];
      select(item);
    };

    const select = async item => {
      query.value = "";
      open.value = false;
      highlighted.value = 0;

      const getTopic = async idOrArray => {
        const id = Array.isArray(idOrArray) ? idOrArray[0] : idOrArray;
        if (!id) return null;
        const doc = await db
          .collection("topics")
          .doc(id)
          .get();
        if (!doc.exists) return null;
        return { id: doc.id, ...doc.data() };
      };

      const fetchDoc = async (collection, id) => {
        if (!collection || !id) return null;
        try {
          const d = await db
            .collection(collection)
            .doc(id)
            .get();
          if (!d.exists) return null;
          return { id: d.id, ...d.data() };
        } catch (err) {
          console.error(`fetchDoc failed for ${collection}/${id}:`, err);
          return null;
        }
      };

      const handleCollectionNavigation = async item => {
        const collection = item.collection;
        const buttonIndex = { events: 2, people: 3, works: 4, terminology: 5 }[
          collection
        ];
        const docCollectionMap = {
          events: "events",
          people: "people",
          works: "works",
          terminology: "terminology"
        };

        try {
          const topic = await getTopic(item.topicID ?? item.document?.topicID);
          const periodValue =
            topic?.timePeriod || item.document?.timePeriod || 1;
          const period =
            Number.isFinite(+periodValue) && +periodValue > 0
              ? +periodValue
              : 1;
          const topicId =
            topic?.id ||
            (Array.isArray(item.topicID) ? item.topicID[0] : item.topicID) ||
            item.document?.topicID ||
            item.document?.id;
          const topicName =
            topic?.title ||
            item.document?.topicName ||
            item.document?.title ||
            "";

          store.dispatch("setTimePeriod", period);
          if (buttonIndex) store.dispatch("setTopicButton", buttonIndex);

          if (topic) await storeTopic.dispatch("setTopicContent", topic);

          const docId = item.id || item.document?.id;
          const doc =
            item.document && item.document.id === docId
              ? item.document
              : await fetchDoc(docCollectionMap[collection], docId);

          if (collection === "events") {
            try {
              await goToEvent(
                {
                  1: "Regional Interactions",
                  2: "First Global Age",
                  3: "Revolutions & Industrialization",
                  4: "Modern Times"
                }[periodValue] || String(periodValue),
                period,
                topicName,
                topicId,
                "Events",
                docId,
                doc?.title || item.document?.title || item.title
              );
            } catch (err) {
              console.error("goToEvent failed:", err, { item });
              pushRoute("Event", {
                period,
                topic: topicId,
                category: "Events",
                event: docId
              });
            }
            return;
          }

          if (collection === "people") {
            try {
              await goToPerson(
                {
                  1: "Regional Interactions",
                  2: "First Global Age",
                  3: "Revolutions & Industrialization",
                  4: "Modern Times"
                }[periodValue] || String(periodValue),
                period,
                topicName,
                topicId,
                "People",
                docId,
                doc?.title || item.document?.title || item.title
              );
            } catch (err) {
              console.error("goToPerson failed:", err, { item });
              pushRoute("Person", {
                period,
                topic: topicId,
                category: "People",
                person: docId
              });
            }
            return;
          }

          if (collection === "works") {
            try {
              await goToTopic(
                {
                  1: "Regional Interactions",
                  2: "First Global Age",
                  3: "Revolutions & Industrialization",
                  4: "Modern Times"
                }[periodValue] || String(periodValue),
                period,
                topic?.title || item.document?.topicName || "",
                topicId,
                "Sources",
                doc?.title || item.document?.title || item.title
              );
              try {
                const current = router.currentRoute.value;
                await router.replace({
                  name: "Topic",
                  params: current.params,
                  query: { scrollTo: docId }
                });
              } catch (e) {
                /* ignore */
              }
            } catch (err) {
              console.error("goToTopic (works) failed:", err, { item });
              pushRoute("Topic", {
                period,
                topic: topicId,
                category: "Sources"
              });
            }
            return;
          }

          if (collection === "terminology") {
            try {
              await goToTopic(
                {
                  1: "Regional Interactions",
                  2: "First Global Age",
                  3: "Revolutions & Industrialization",
                  4: "Modern Times"
                }[periodValue] || String(periodValue),
                period,
                topic?.title || item.document?.topicName || "",
                topicId,
                "Terms",
                doc?.title || item.document?.title || item.title
              );
              try {
                const current = router.currentRoute.value;
                await router.replace({
                  name: "Topic",
                  params: current.params,
                  query: { scrollTo: docId }
                });
              } catch (e) {
                /* ignore */
              }
            } catch (err) {
              console.error("goToTopic (terminology) failed:", err, { item });
              pushRoute("Topic", { period, topic: topicId, category: "Terms" });
            }
            return;
          }
        } catch (err) {
          console.error("Search navigation (collection) failed:", err, {
            item
          });
        }
      };

      switch (item.collection) {
        case "topics": {
          // For "topics" search items the item itself IS the topic; item.document
          // is only populated for sub-items (events/people/etc). Prefer item.id.
          const topicID = item.id || item.document?.id;

          const topicDoc =
            (item.document && item.document.id === topicID) || item.document
              ? item.document
              : await getTopic(topicID);

          // The search index's own timePeriod can be stale/incorrect, so prefer
          // the freshly-fetched topic doc's timePeriod (same pattern as
          // handleCollectionNavigation below).
          const periodRaw =
            topicDoc?.timePeriod ??
            item.timePeriod ??
            item.document?.timePeriod ??
            1;
          const period =
            Number.isFinite(+periodRaw) && +periodRaw > 0 ? +periodRaw : 1;
          const topicName = topicDoc?.title || item.title;

          const periodTitles = {
            1: "Regional Interactions",
            2: "First Global Age",
            3: "Revolutions & Industrialization",
            4: "Modern Times"
          };
          const timePeriodTitle = periodTitles[period] ?? "Period";

          await store.dispatch("setTimePeriod", period);

          if (topicDoc) await storeTopic.dispatch("setTopicContent", topicDoc);

          try {
            // Preserve the topic's unit so Topics.vue selects the right unit if goToTopic fails below
            await goToPeriod(timePeriodTitle, period, { unit: topicDoc?.unit || "" });
          } catch (err) {
            if (!err || err.name !== "NavigationDuplicated") throw err;
          }

          try {
            await goToTopic(
              timePeriodTitle,
              period,
              topicName,
              topicID,
              "Introduction"
            );
          } catch (err) {
            if (!err || err.name !== "NavigationDuplicated") throw err;
          }

          break;
        }

        case "events": {
          await handleCollectionNavigation(item);
          break;
        }

        case "people": {
          await handleCollectionNavigation(item);
          break;
        }

        case "works": {
          await handleCollectionNavigation(item);
          break;
        }

        case "terminology": {
          await handleCollectionNavigation(item);
          break;
        }

        default:
          console.warn("Unhandled search type:", item.collection);
      }
    };

    onMounted(() => {
      grabbingSearch();
    });

    return {
      query,
      open,
      filtered,
      highlighted,
      handleSearchFocus,
      handleSearchInput,
      move,
      enterSelected,
      select,
      prettyType,
      badgeClass,
      displayTitle,
      closeLater
    };
  }
};
</script>

<style scoped lang="sass" src="@/assets/css/navbar.sass"></style>
<style scoped lang="sass">
.search-bar-wrapper
  width: 280px
  border: 1.5px solid #33401a
  border-radius: 8px

.search-bar
  width: 100%
  height: 38px
  border-radius: 8px
  border: none
  background: #fff

.search-input
  font-size: 14px

.typeahead
  width: 100%
  min-width: 280px
  z-index: 10000
</style>
